import { AsyncValidator, SyncValidator } from './validators';
import { TemplateResult } from 'lit';

/**
 * Interface used for external node configuration
 */
export interface NodeConfiguration {
  /**
   * Unique node id.
   * Default: undefined.
   */
  nodeId: string;

  /**
   * Node name that will be presented in the ui.
   * Default: undefined.
   */
  nodeName: string;

  /**
   * Icon that is set before the nodeName.
   * Default: undefined.
   */
  icon?: string;

  /**
   * If set to true, the node will not display a checkbox, even if `checkbox` property is enabled in the tree dataSource
   * config.
   */
  noCheckbox?: boolean;

  /**
   * If set to true, no request will be triggered for fetching child node.
   */
  leaf?: boolean;

  /**
   * List of validators for drag and drop.
   */
  dropValidators?: Array<SyncValidator | AsyncValidator>;

  /**
   * Property to attach original data to a node. Since the node data is limited to id and name, there might be instances
   * where additional data needs to be linked to a node for backend interaction.
   * The original data won't be altered in case of changes (drag and drop, etc.).
   * It will be up to the developer to update the original data after the change event occurred.
   */
  originalData?: any;

  /**
   * Specifies if the node lives outside of the Tree.
   * Used for drag and drop in order to add an external node to the tree.
   */
  external?: boolean;

  /**
   * Specifies if the external node should be attached to the tree.
   */
  attachToTree?: boolean;

  /**
   * Flag to store a temporary node. There are cases when a node is added to the tree as a placeholder (Example create new node),
   * and then replaced with the created node.
   */
  temporaryNode?: boolean;

  /**
   * If set, the node will render a new custom element
   */
  namePrefixRenderer?: () => TemplateResult;

  page?: number;
}

/**
 * Interface used for a node data that extends the node configuration.
 * This interface is used internally by the Tree and should not be used from outside.
 */
export interface NodeData extends NodeConfiguration {
  /**
   * Sets the node as open or closed. Used internally by the tree.
   * Default: false.
   */
  isOpen?: boolean;

  /**
   * List of child id's for the current node.
   */
  childNodeIds?: Array<string>;

  /**
   * Specifies the id of the parent node. undefined if root.
   */
  parentNodeId?: string;

  /**
   * Specifies the id of the parent node name. undefined if root.
   */
  parentName?: string;

  /**
   * Indicates if a node has been clicked or not.
   */
  active?: boolean;

  /**
   * Checks if the node is loading
   */
  isLoading?: boolean;

  /**
   * Checks if the node can be clickable or toggled.
   * Example: for cannot be managed by above companies in network tree.
   */
  isDisabled?: boolean;

  /**
   * Edit state for the node.
   */
  editState?: boolean | undefined;

  /**
   * Sets the currently loaded page for the node.
   */
  loadedPage?: number;

  hasMoreChildNodes?: boolean;
  /**
   * Specifies if the next page is loading, used for nodes that have pagination.
   */
  isNextPageLoading?: boolean;
}

export interface SelectedNodes<T> {
  key: string;
  value: T;
}

export interface NodeOptions {
  /**
   * Current node structure.
   */
  currentNode: NodeConfiguration;

  /**
   * Direct child nodes of the current node.
   */
  childNodes: Array<NodeConfiguration>;
  hasMoreChildNodes?: boolean;
  page?: number;
}

export enum TreeDataSourceType {
  STATIC = 'static',
  DYNAMIC = 'dynamic',
}

export interface TreeDataSourceOptions {
  /**
   * Root node ids which are used as an entry point to fetch subnodes.
   */
  rootNodeIds: Array<string>;

  /**
   * Enables drag and drop functionality.
   */
  dragAndDropEnabled?: boolean;

  /**
   * Enables selection functionality.
   */
  checkbox?: boolean;

  /**
   * Enables single select checkbox
   */
  singleSelect?: boolean;

  /**
   * Method that is called after a node has been moved in the tree but before the state has been updated.
   * Returning Promise of true will cause the state to update, returning false will prevent the nodes from being updated.
   * By default the method is implemented to return true if handler is omitted.
   */
  requestMoveNodeHandler?: (node: NodeData) => Promise<boolean>;
}

export interface DynamicTreeDataSourceOptions extends TreeDataSourceOptions {
  /**
   * Controls the refetching of data when a node is clicked.
   * Has no effect on StaticDataSource
   * Default value: true.
   */
  cacheEnabled?: boolean;
  requestNodeData: (data: NodeData) => Promise<NodeOptions | null>;
  requestRootNodeData: (nodeId: string) => Promise<NodeOptions | null>;
  hasPagination?: boolean;
}

export interface StaticTreeDataSourceOptions extends TreeDataSourceOptions {
  requestStaticNodes: (nodeId: string) => Promise<Array<StaticNode> | null>;
}

export interface StaticNode extends NodeConfiguration {
  /**
   * Sets the node as open or closed. Used internally by the tree.
   * Default: false.
   */
  isOpen?: boolean;

  /**
   * List of child nodes that will be appended to the tree.
   */
  childNodes?: Array<StaticNode>;
  parentNodeId?: string; // Optional property added to track the parent node.
}

export class NodeDataUtils {
  /**
   * Method that converts a nodeData to a nodeConfig, in order to not expose internal tree data outside when it's not needed.
   */
  public static convertToNodeConfig(nodeData: NodeData): NodeConfiguration {
    const { nodeId, nodeName, attachToTree, dropValidators, external, icon, leaf, originalData, temporaryNode } =
      nodeData;

    return {
      nodeId,
      nodeName,
      attachToTree,
      dropValidators,
      external,
      icon,
      leaf,
      originalData,
      temporaryNode,
    };
  }
}
